<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import Store from '@store/dataProvider';
import { IPolicy } from '../../types/policy.type';
import { IClient } from '../../types/client.type';
import { useI18n } from 'vue-i18n';
import CertificatesRepository from '@repositories/certificates.repository';
import moment from 'moment';
import ModalDetail from './ModalDetail.vue';
import storeAuth from "@store/auth";
import { useToast } from '../../composables/useToast';
import DataTable from '@components/DataTable.vue';

const isLoadingCertificates = ref<boolean>(false);
const isDownloadingExcel = ref<boolean>(false);

const { verifyPermission } = storeAuth()

const { t } = useI18n();
const store = Store();

const { $toast } = useToast();
const certificatesRepository = new CertificatesRepository();
const certificatesData = ref([]);
const paginationData = ref();
const currentPage = ref<number>(1);
const isModalOpen = ref<boolean>(false);
const selectedCertificateInfo = ref<IClient | null>(null);
const selectedPolicyInfo = ref<IPolicy | null>(null);
const modalType = ref<string>('');
const refTableForm = ref<any>(null);
const filterData = ref<any>(null);

const fieldsFiltersExel: any = ref([
  {
    key: "search",
    title: t('global.buscar'),
    type: "text",
    cols: 6,
  },
  {
    key: 'startDate',
    title: t('polizas.polizas.fecha_de_inicio'),
    type: 'datetime',
    enableTimePicker: false,
    format: 'dd/MM/yyyy',
    modelType: 'yyyy-MM-dd',
    autoApply: true,
    listenChange: true,
    onDatePicker: true,
    placeholder: t('polizas.polizas.fecha_de_inicio'),
    value: null,
    cols: 2,
  },
  {
    key: 'endDate',
    title: t('polizas.polizas.fecha_de_fin'),
    type: 'datetime',
    format: 'dd/MM/yyyy',
    modelType: 'yyyy-MM-dd',
    autoApply: true,
    enableTimePicker: false,
    disabled: true,
    placeholder: t('polizas.polizas.fecha_de_fin'),
    value: null,
    cols: 2,
  },
  {
    key: 'status',
    title: t('listado_de_certificados.state'),
    type: 'select',
    label: 'status',
    formatValue: (val: any) => val?.status,
    placeholder: t('listado_de_certificados.state'),
    value: null,
    selectLabel: null,
    cols: 2,
    options: [
      { id: 1, status: 'EMITIDO' },
      { id: 2, status: 'CANCELADO' },
    ],
  },
]);

const handleAllowEndDate = (e: any) => {
  if(e.input.key === 'startDate') {
    fieldsFiltersExel.value.forEach((field: any) => {
      if(field.key == 'endDate' && e.value !== null) {
        field.disabled = false
        const startDate = new Date(e.value);
        const minEndDate = new Date(startDate);
        minEndDate.setDate(startDate.getDate() + 1);
        field.min = minEndDate;
      } else if (field.key == 'endDate' && e.value === null) {
        field.disabled = true
        field.min = null;
        refTableForm.value.setValue({ key: 'endDate', value: null})
      }
    });
  }
};

const getCertificates = async (page: number) => {
  isLoadingCertificates.value = true;
  try {
    const { data } = await certificatesRepository.getCertificates({
      page,
      searchData: filterData.value,
    });
    certificatesData.value = data.certificates ?? [];
    paginationData.value = data.pagination;
    store.setProp({ key: 'certificates', value: certificatesData.value });
  } finally {
    isLoadingCertificates.value = false;
  }
};

const handleSearch = async () => {
  const { items } = await refTableForm.value.getValues()
  filterData.value = items;

  getCertificates(1);
};

onMounted(async () => {
  getCertificates(1);
});
const fields: any = ref([
  {
    key: 'id',
    label: t('listado_de_certificados.certificado_numero'),
    sortable: true,
  },
  {
    key: 'onBehalfOf',
    label: t('listado_de_certificados.por_cuenta_de'),
  },
  {
    key: 'insuranceBeneficiary',
    label: t('listado_de_certificados.beneficiario_seguro'),
  },
  {
    key: 'createdByUser',
    label: t('listado_de_certificados.creado_por'),
	 formatter: (e: any) => e?.fullName,
  },
  {
    key: 'status',
    label: t('listado_de_certificados.state'),
  },
  {
    key: 'issueDate',
    label: t('listado_de_certificados.fecha_de_emision'),
    formatter: (e: any) =>
      e ? moment(e).format('DD-MM-YYYY HH:mm') : 'Sin fecha',
  },
  {
    key: 'certificateInfo',
    label: t('listado_de_certificados.detalle'),
  },
  {
    key: 'policyInfo',
    label: t('listado_de_certificados.descargar'),
  },
  {
    key: 'actions',
    label: t('global.acciones'),
  },
]);

const onChangePage = async (page: any) => {
  currentPage.value = page;
  getCertificates(page);
};
const handleSelectClient = (certificateInfo: any) => {
  selectedCertificateInfo.value = certificateInfo;
  modalType.value = 'modalCertificate';
  isModalOpen.value = true;
};
const handleSelectPolicy = async(certificateInfo: any) => {
  await certificatesRepository.downloadPDF(certificateInfo.id, certificateInfo.insuranceBeneficiary);

};

const handleClose = () => {
  isModalOpen.value = false;
  selectedCertificateInfo.value = null;
  selectedPolicyInfo.value = null;
  modalType.value = '';
};

const handleChangeState = async ({ id }: any) => {
  try {
    const data = await certificatesRepository.updateCertifcate(id);

    let index = store.certificates.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'certificates', index, value: data });
      $toast?.success(t('listado_de_certificados.estado_cancelado'));
    }
  } catch (error: any) {
    $toast?.error(error?.message ?? t('global.ocurrio_un_error'));
  }
};
const handleResetFiltersForm = async ({ id }: any) => {
  await refTableForm.value.resetValues();
  filterData.value = null;
  getCertificates(1);
};

const downloadExcel = async ({ items, isFormValid }: any) => {
  if (!isFormValid || isDownloadingExcel.value) return;

  const { search, startDate, endDate, status } = items;
  isDownloadingExcel.value = true;
  try {
    await certificatesRepository.getExcelPolicies({
      search,
      toExcel: true,
      startDate,
      endDate,
      status: status,
    });
  } finally {
    isDownloadingExcel.value = false;
  }
};


</script>

<template>
  <Layout
    :title="t('listado_de_certificados.listado_de_certificados')"
    :textColor="'color-secondary'"
  >
    <template #layout>
      <Form
        ref="refTableForm"
        :inputs="fieldsFiltersExel"
        @onSubmit="downloadExcel"
        @datePicker="handleAllowEndDate"
        customClass="poliza__form"
      >
        <template #buttons>
          <CButton
            class="background-blue button__form"
            type="button"
            style="padding: 0.6rem !important; margin: 0.6rem"
            @click="handleSearch"
          >
            <template #content>{{ t('global.buscar') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.55rem !important"
            @click="handleResetFiltersForm"
            type="button"
          >
            <template #content>{{ t('global.limpiar_filtro') }}</template>
          </CButton>
          <CButton
            class="background-blue button__form position-relative"
            style="padding: 0.55rem !important"
            :disabled="isDownloadingExcel"
            :class="{ disabled: isDownloadingExcel }"
            @click="downloadExcel"
          >
            <template #content>
              <span
                v-if="isDownloadingExcel"
                class="position-absolute top-50 start-50 translate-middle d-flex"
              >
                <BSpinner small />
              </span>
              <span :class="{ invisible: isDownloadingExcel }">{{ t('global.descargar_excel') }}</span>
            </template>
          </CButton>
        </template>
      </Form>
      <DataTable
        :loading="isLoadingCertificates"
        :fields="fields"
        :items="store.certificates"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-if="true" v-slot:cell(certificateInfo)="certificatesData">
          <div @click="handleSelectClient(certificatesData.data.item)">
			  <img
			  src="/icons/policy_panel.svg"
			  style="width: 40px; cursor: pointer"
			  />
			</div>
        </template>
        <template v-if="true" v-slot:cell(policyInfo)="certificatesData">
			<div @click="handleSelectPolicy(certificatesData.data.item)">
			  <img
				src="/icons/pdf.svg"
				style="width: 40px; cursor: pointer"
			  />
          </div>
        </template>
        <template v-if="verifyPermission('Cancelar Certificados')" v-slot:cell(actions)="certificatesData">
          <div>
            <button
              class="background-pink p-1 border-0 w-100 text-white rounded-4 uppercase"
              @click="handleChangeState(certificatesData.data.item)"
              :disabled="certificatesData.data.item?.status === 'CANCELADO'"
            >
            {{ t('global.cancelar') }}
            </button>
          </div>
        </template>
      </DataTable>
      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="
          modalType === 'modalCertificate'
            ? t('operaciones.operaciones.informacion_del_cliente')
            : t('operaciones.operaciones.informacion_de_la_poliza')
        "
      >
        <template v-if="modalType === 'modalCertificate'">
          <ModalDetail
            :data="selectedCertificateInfo"
            v-if="selectedCertificateInfo"
          />
        </template>
      </BModal>
    </template>
  </Layout>
</template>

<style scoped>
.button__form.disabled {
  opacity: 0.4;
}
</style>
